import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Inbox, FileText, MessageSquare, CircleDot, Phone, Mail, RefreshCw, Check, Download, Ban, ShieldCheck,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_COLORS = {
  new: "bg-brand-clay text-white",
  contacted: "bg-brand-sand text-brand-ink",
  closed: "bg-brand-moss text-white",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, quotes: 0, contacts: 0 });
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("leads");
  const [events, setEvents] = useState([]);
  const [complianceLoading, setComplianceLoading] = useState(false);

  const loadCompliance = useCallback(async () => {
    setComplianceLoading(true);
    try {
      const res = await api.get("/admin/compliance-log");
      setEvents(res.data.events || []);
    } catch {
      toast.error("Failed to load compliance log");
    } finally {
      setComplianceLoading(false);
    }
  }, []);

  const exportCompliance = () => {
    const rows = [["Date", "Event", "Name", "Phone", "Source", "Details", "Consent Version"]];
    events.forEach((e) => rows.push([
      e.at ? new Date(e.at).toISOString() : "",
      e.kind === "opt_out" ? "Opt-out" : "Opt-in",
      e.name || "", e.phone || "", e.source || "",
      (e.detail || "").replace(/"/g, "'"), e.consent_version || "",
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `consent-optout-log-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    toast.success("Compliance log exported");
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== "all") params.type = typeFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const [s, st] = await Promise.all([
        api.get("/admin/submissions", { params }),
        api.get("/admin/stats"),
      ]);
      setSubs(s.data);
      setStats(st.data);
    } catch {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/submissions/${id}`, { status });
      toast.success(`Marked as ${status}`);
      await load();
    } catch {
      toast.error("Update failed");
    }
  };

  const toggleDnc = async (s) => {
    const next = !s.do_not_contact;
    try {
      await api.patch(`/admin/submissions/${s.id}/dnc`, { do_not_contact: next, note: "Set from dashboard" });
      toast.success(next ? "Marked Do Not Contact" : "Contact allowed again");
      await load();
    } catch {
      toast.error("Update failed");
    }
  };

  const doLogout = () => { logout(); nav("/admin/login"); };

  const exportCsv = async () => {
    try {
      const params = {};
      if (typeFilter !== "all") params.type = typeFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await api.get("/admin/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `doug-jones-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Leads exported to CSV");
    } catch {
      toast.error("Export failed");
    }
  };

  const STAT_CARDS = [
    { icon: Inbox, label: "Total leads", value: stats.total },
    { icon: CircleDot, label: "New / unread", value: stats.new },
    { icon: FileText, label: "Quote requests", value: stats.quotes },
    { icon: MessageSquare, label: "Contact messages", value: stats.contacts },
  ];

  return (
    <div className="min-h-screen bg-brand-cream">
      {/* Top bar */}
      <header className="sticky top-0 z-10 bg-brand-green text-brand-cream">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="grid h-8 w-8 place-items-center rounded-full bg-brand-sand text-brand-ink font-serif">D</span>
            <span className="font-serif text-lg">Lead Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-brand-cream/70">{user?.email}</span>
            <button onClick={doLogout} data-testid="logout-btn"
              className="flex items-center gap-2 text-sm rounded-full border border-brand-cream/30 px-4 py-2 hover:bg-brand-cream/10 transition-colors">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        {/* View toggle */}
        <div className="flex gap-2 mb-8">
          <button onClick={() => setView("leads")} data-testid="view-leads"
            className={`rounded-full text-sm font-medium px-5 h-10 transition-all ${view === "leads" ? "bg-brand-green text-brand-cream" : "border border-brand-line text-brand-moss hover:bg-brand-sand/30"}`}>
            Leads
          </button>
          <button onClick={() => { setView("compliance"); loadCompliance(); }} data-testid="view-compliance"
            className={`flex items-center gap-2 rounded-full text-sm font-medium px-5 h-10 transition-all ${view === "compliance" ? "bg-brand-green text-brand-cream" : "border border-brand-line text-brand-moss hover:bg-brand-sand/30"}`}>
            <ShieldCheck className="h-4 w-4" /> Consent &amp; Opt-Out Log
          </button>
        </div>

        {view === "leads" && (<>
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {STAT_CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.label} data-testid={`stat-${c.label}`}
                className="rounded-2xl border border-brand-line bg-white p-6">
                <Icon className="h-6 w-6 text-brand-clay" />
                <div className="font-serif text-4xl text-brand-ink mt-3">{c.value}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-brand-moss mt-1">{c.label}</div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <span className="overline">Submissions</span>
          <div className="flex-1" />
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40 bg-white border-brand-line" data-testid="filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="quote">Quote requests</SelectItem>
              <SelectItem value="contact">Contact messages</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-white border-brand-line" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <button onClick={load} data-testid="refresh-btn"
            className="grid h-10 w-10 place-items-center rounded-full border border-brand-line bg-white hover:bg-brand-sand/30 transition-colors">
            <RefreshCw className={`h-4 w-4 text-brand-green ${loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={exportCsv} data-testid="export-btn"
            className="flex items-center gap-2 rounded-full bg-brand-green text-brand-cream text-sm font-medium px-4 h-10 hover:bg-brand-ink active:scale-95 transition-all">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>

        {/* List */}
        {loading ? (
          <div className="py-20 text-center text-brand-moss">Loading…</div>
        ) : subs.length === 0 ? (
          <div className="py-20 text-center text-brand-moss" data-testid="empty-state">No submissions yet.</div>
        ) : (
          <div className="space-y-4" data-testid="submissions-list">
            {subs.map((s) => (
              <div key={s.id} data-testid={`submission-${s.id}`}
                className="rounded-2xl border border-brand-line bg-white p-6 grid lg:grid-cols-12 gap-4 items-start">
                <div className="lg:col-span-3">
                  <div className="flex items-center gap-2">
                    <Badge className={`${STATUS_COLORS[s.status]} border-0 rounded-full font-mono text-[10px] uppercase`}>{s.status}</Badge>
                    <Badge variant="outline" className="rounded-full font-mono text-[10px] uppercase border-brand-line text-brand-moss">
                      {s.type === "quote" ? (s.line_of_business || "quote") : "contact"}
                    </Badge>
                  </div>
                  <div className="font-serif text-xl text-brand-ink mt-2">{s.name}</div>
                  <div className="mt-1 space-y-0.5 text-sm text-brand-moss">
                    <a href={`mailto:${s.email}`} className="flex items-center gap-1.5 hover:text-brand-clay"><Mail className="h-3.5 w-3.5" /> {s.email}</a>
                    <a href={`tel:${s.phone}`} className="flex items-center gap-1.5 hover:text-brand-clay"><Phone className="h-3.5 w-3.5" /> {s.phone}</a>
                  </div>
                </div>

                <div className="lg:col-span-6">
                  <p className="text-brand-ink text-sm leading-relaxed">{s.message || <span className="text-brand-moss/60 italic">No message</span>}</p>
                  <div className="mt-3 flex flex-wrap gap-3 text-[11px] font-mono text-brand-moss items-center">
                    <span className={s.sms_opt_in ? "text-brand-green" : "text-brand-moss/50"}>
                      SMS opt-in: {s.sms_opt_in ? "YES" : "no"}
                    </span>
                    {s.sms_opt_in && s.consent_version && (
                      <span title={s.consent_text || ""} className="text-brand-moss/70">
                        · consent {s.consent_version}{s.consent_at ? ` @ ${new Date(s.consent_at).toLocaleDateString()}` : ""}
                      </span>
                    )}
                    <span>· {new Date(s.created_at).toLocaleString()}</span>
                    {s.do_not_contact && (
                      <Badge className="bg-destructive text-white border-0 rounded-full font-mono text-[10px] uppercase">Do Not Contact</Badge>
                    )}
                  </div>
                </div>

                <div className="lg:col-span-3 flex flex-wrap lg:justify-end gap-2">
                  {s.status !== "contacted" && (
                    <button onClick={() => setStatus(s.id, "contacted")} data-testid={`mark-contacted-${s.id}`}
                      className="rounded-full bg-brand-sand text-brand-ink text-xs font-medium px-4 py-2 hover:brightness-95 transition-all">
                      Mark contacted
                    </button>
                  )}
                  {s.status !== "closed" && (
                    <button onClick={() => setStatus(s.id, "closed")} data-testid={`mark-closed-${s.id}`}
                      className="rounded-full border border-brand-line text-brand-moss text-xs font-medium px-4 py-2 flex items-center gap-1 hover:bg-brand-green hover:text-brand-cream transition-all">
                      <Check className="h-3.5 w-3.5" /> Close
                    </button>
                  )}
                  <button onClick={() => toggleDnc(s)} data-testid={`toggle-dnc-${s.id}`}
                    className={`rounded-full text-xs font-medium px-4 py-2 flex items-center gap-1 transition-all ${s.do_not_contact ? "bg-brand-green text-brand-cream hover:bg-brand-ink" : "border border-destructive/40 text-destructive hover:bg-destructive hover:text-white"}`}>
                    <Ban className="h-3.5 w-3.5" /> {s.do_not_contact ? "Allow contact" : "Do not contact"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        </>)}

        {view === "compliance" && (
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="overline">Consent &amp; opt-out audit</span>
              <div className="flex-1" />
              <button onClick={loadCompliance} data-testid="compliance-refresh"
                className="grid h-10 w-10 place-items-center rounded-full border border-brand-line bg-white hover:bg-brand-sand/30 transition-colors">
                <RefreshCw className={`h-4 w-4 text-brand-green ${complianceLoading ? "animate-spin" : ""}`} />
              </button>
              <button onClick={exportCompliance} data-testid="compliance-export"
                className="flex items-center gap-2 rounded-full bg-brand-green text-brand-cream text-sm font-medium px-4 h-10 hover:bg-brand-ink active:scale-95 transition-all">
                <Download className="h-4 w-4" /> Export Log
              </button>
            </div>

            {complianceLoading ? (
              <div className="py-20 text-center text-brand-moss">Loading…</div>
            ) : events.length === 0 ? (
              <div className="py-20 text-center text-brand-moss" data-testid="compliance-empty">No consent or opt-out events yet.</div>
            ) : (
              <div className="rounded-2xl border border-brand-line bg-white overflow-hidden" data-testid="compliance-list">
                <div className="hidden sm:grid grid-cols-12 gap-2 px-5 py-3 bg-brand-sand/30 font-mono text-[10px] uppercase tracking-wider text-brand-moss">
                  <div className="col-span-3">Date</div>
                  <div className="col-span-2">Event</div>
                  <div className="col-span-3">Name / Phone</div>
                  <div className="col-span-4">Details</div>
                </div>
                {events.map((e, i) => (
                  <div key={i} data-testid={`compliance-row-${i}`}
                    className="grid grid-cols-1 sm:grid-cols-12 gap-2 px-5 py-3 border-t border-brand-line text-sm sm:items-center">
                    <div className="sm:col-span-3 text-brand-moss">{e.at ? new Date(e.at).toLocaleString() : "—"}</div>
                    <div className="sm:col-span-2">
                      <Badge className={`border-0 rounded-full font-mono text-[10px] uppercase ${e.kind === "opt_out" ? "bg-destructive text-white" : "bg-brand-green text-brand-cream"}`}>
                        {e.kind === "opt_out" ? "Opt-out" : "Opt-in"}
                      </Badge>
                    </div>
                    <div className="sm:col-span-3">
                      <div className="text-brand-ink">{e.name || "—"}</div>
                      <div className="text-brand-moss text-xs font-mono">{e.phone || ""}</div>
                    </div>
                    <div className="sm:col-span-4 text-brand-moss">
                      {e.detail}{e.consent_version ? ` · ${e.consent_version}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
